// Projekt-Übersicht — nur die Projekt-Card-Liste, ohne Dashboard-Zeug.
// Vorher war die in Home.tsx mit eingebettet, jetzt eigene Route /projects
// für die Sidebar-Restruktur (Sprint B Story 1).
//
// Redesign-Paket 2: Der Seitenkopf ist nicht mehr selbst gebaut, sondern wird
// über `usePageHeader` in die EINE App-Kopfzeile geschrieben (Titel, Untertitel,
// „Neues Projekt", Status-Tabs). Der Inhalt liegt in `PageShell` — vorher hatte
// diese Route GAR KEIN `max-w` und lief auf breiten Bildschirmen auseinander.

import { useEffect, useState } from "preact/hooks";
import { Plus, FolderOpen } from "lucide-preact";
import { Layout } from "../components/Layout";
import { ProjectCard } from "../components/ProjectCard";
import { NewProjectModal } from "../components/NewProjectModal";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { PageShell } from "../components/ui/PageShell";
import { ViewTabs } from "../components/ui/ViewTabs";
import { usePageHeader } from "../lib/pageHeader";
import { requireLabel } from "../lib/navigation";
import { useProjects } from "../store/projects";
import { useUserPrefs } from "../store/userPrefs";
import { isProjectVisible } from "../lib/departmentFilter";
import type { ProjectStatus } from "@juuplan/shared";
import { uxEnabled } from "../lib/ux";
import { fetchPortfolio } from "../lib/uxApi";
import { localTodayIso } from "../lib/format";
import type { PortfolioProjectDto } from "@juuplan/shared";
import {
  buildPortfolioRows,
  needsAttention,
  DEFAULT_PORTFOLIO_SORT,
  type PortfolioSort,
} from "../lib/portfolio";
import { PortfolioTable } from "../components/projects/PortfolioTable";

// UX-Vorschlag P4: gemerkte Ansicht (Liste/Karten) — reine Bequemlichkeit pro
// Browser, darum localStorage mit try/catch.
const DISPLAY_KEY = "juuplan:projects-display";
type Display = "list" | "cards";
function loadDisplay(): Display {
  try {
    return localStorage.getItem(DISPLAY_KEY) === "cards" ? "cards" : "list";
  } catch {
    return "list";
  }
}
function saveDisplay(d: Display): void {
  try {
    localStorage.setItem(DISPLAY_KEY, d);
  } catch {
    /* ignore */
  }
}

type Tab = "all" | "active" | "paused" | "done" | "archived";

const TABS: { id: Tab; label: string }[] = [
  { id: "all", label: "Alle" },
  { id: "active", label: "Aktiv" },
  { id: "paused", label: "Pausiert" },
  { id: "done", label: "Abgeschlossen" },
  { id: "archived", label: "Archiviert" },
];

export function Projects(): preact.JSX.Element {
  const projects = useProjects();
  const prefs = useUserPrefs();
  const [tab, setTab] = useState<Tab>("active");
  const [showCreate, setShowCreate] = useState(false);

  // ---- P4 (nur bei aktivem Flag) ----
  const p4 = uxEnabled("ux_web_p4");
  const [display, setDisplay] = useState<Display>(loadDisplay);
  const [attentionOnly, setAttentionOnly] = useState(true);
  const [sort, setSort] = useState<PortfolioSort>(DEFAULT_PORTFOLIO_SORT);
  const [metrics, setMetrics] = useState<PortfolioProjectDto[] | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  useEffect(() => {
    if (!p4) return;
    let alive = true;
    setMetricsError(null);
    fetchPortfolio(localTodayIso())
      .then((r) => alive && setMetrics(r.projects))
      .catch((e: unknown) => {
        // GET-Fehler toastet api() nicht: sichtbar machen, Zahlen bleiben „—".
        if (alive) setMetricsError(e instanceof Error ? e.message : "Unbekannter Fehler");
      });
    return () => {
      alive = false;
    };
  }, [p4]);

  // Sprint 3 Story 3: Department-Filter zuerst, dann Status-Tab.
  const inDepartmentScope = projects.items.filter((p) =>
    isProjectVisible(p, prefs.visible_departments)
  );

  const matches = (p: { status: ProjectStatus }): boolean => {
    if (tab === "all") return true;
    return p.status === tab;
  };

  const visible = inDepartmentScope.filter(matches);
  const portfolioRows = p4 ? buildPortfolioRows(visible, metrics) : [];
  const attentionCount = portfolioRows.filter(needsAttention).length;
  const shownRows = attentionOnly ? portfolioRows.filter(needsAttention) : portfolioRows;
  const showList = p4 && display === "list";
  const counts: Record<Tab, number> = {
    all: inDepartmentScope.length,
    active: inDepartmentScope.filter((p) => p.status === "active").length,
    paused: inDepartmentScope.filter((p) => p.status === "paused").length,
    done: inDepartmentScope.filter((p) => p.status === "done").length,
    archived: inDepartmentScope.filter((p) => p.status === "archived").length,
  };

  // Die Status-Umschaltung ist eine Ansichtswahl über EINE Liste — sie gehört
  // laut Referenz (docs/design/01-roadmap-expanded.png) in den Kopf, nicht als
  // zweite Leiste in den Inhalt. Verhalten unverändert: dieselben fünf Reiter,
  // dieselben Zähler.
  usePageHeader({
    title: requireLabel("/projects"),
    subtitle: "Alle Projekte deiner Organisation und ihrer Abteilungen.",
    actions: (
      <Button variant="primary" size="lg" icon={Plus} onClick={() => setShowCreate(true)}>
        Neues Projekt
      </Button>
    ),
    tabs: (
      <ViewTabs
        label="Projektstatus"
        items={TABS.map((t) => ({ id: t.id, label: t.label, badge: counts[t.id] }))}
        active={tab}
        onChange={(id) => setTab(id as Tab)}
      />
    ),
  });

  return (
    <Layout>
      <PageShell width="default">
        {projects.loading && projects.items.length === 0 ? (
          <p class="text-sm text-[color:var(--color-on-surface-variant)] italic">
            Lädt…
          </p>
        ) : visible.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title="Keine Projekte in dieser Kategorie."
            description="Wechsle den Reiter oben oder leg ein neues Projekt an."
            action={
              <Button variant="primary" icon={Plus} onClick={() => setShowCreate(true)}>
                Neues Projekt
              </Button>
            }
          />
        ) : p4 ? (
          <>
            <div class="mb-4 flex flex-wrap items-center gap-2">
              {showList && (
                <button
                  type="button"
                  aria-pressed={attentionOnly}
                  onClick={() => setAttentionOnly(!attentionOnly)}
                  class={`rounded-full border px-3 py-1 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                    attentionOnly
                      ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary-strong)]"
                      : "border-[color:var(--color-outline-variant)] text-[color:var(--color-on-surface-variant)]"
                  }`}
                >
                  Braucht Aufmerksamkeit ({attentionCount})
                </button>
              )}
              <div class="ml-auto inline-flex rounded border border-[color:var(--color-outline-variant)]" role="group" aria-label="Darstellung">
                {(["list", "cards"] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={display === d}
                    onClick={() => {
                      setDisplay(d);
                      saveDisplay(d);
                    }}
                    class={`px-3 py-1 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                      display === d
                        ? "bg-[color:var(--color-surface-container-high)] font-medium text-[color:var(--color-on-surface)]"
                        : "text-[color:var(--color-on-surface-variant)]"
                    }`}
                  >
                    {d === "list" ? "Liste" : "Karten"}
                  </button>
                ))}
              </div>
            </div>
            {showList && metricsError && (
              <p class="mb-3 text-sm text-[color:var(--color-error)]">
                Kennzahlen (überfällig, blockiert, Meilensteine) konnten nicht geladen werden: {metricsError}
              </p>
            )}
            {showList ? (
              shownRows.length === 0 ? (
                <EmptyState
                  icon={FolderOpen}
                  title="Nichts braucht gerade Aufmerksamkeit."
                  description="Kein Projekt in diesem Reiter ist überfällig, blockiert oder in Verzug."
                  action={
                    <Button variant="secondary" onClick={() => setAttentionOnly(false)}>
                      Alle anzeigen
                    </Button>
                  }
                />
              ) : (
                <PortfolioTable rows={shownRows} sort={sort} onSort={setSort} />
              )
            ) : (
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {visible.map((p) => (
                  <ProjectCard key={p.id} project={p} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visible.map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        )}
      </PageShell>

      {showCreate && (
        <NewProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={() => setShowCreate(false)}
        />
      )}
    </Layout>
  );
}
